import { useAppDispatch, useAppSelector } from "@/core/app/hooks"
import { getFormByName } from "@/core/app/slices/uassist/form.util"
import { fetchFormByCaseIdWithMergeData } from "@/core/app/slices/uassist/formThunkApi"
import { RootState } from "@/core/app/store"
import { parseToObject } from "@/core/utils/formatters"
import { JsonSchema, UISchemaElement } from "@jsonforms/core"
import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react"
import { FormRenderMode, RenderedTemplateType, FormContextType, FormProviderProps, FormType } from "./types"

const FormContext = createContext<FormContextType | undefined>(undefined)

/**
 * Provides form state and actions to its children via React context.
 */
export const FormProvider: React.FC<FormProviderProps> = ({
  children,
  details,
  showValidationErrors: showValidationErrorsProp = false,
  mode: modeProp = FormRenderMode.EDIT,
}) => {
  const dispatch = useAppDispatch()

  const { formTemplateList } = useAppSelector(
    (state: RootState) => state.formService,
  )
  const [renderedTemplate, setRenderedTemplate] =
    useState<RenderedTemplateType>(null)

  const [formType, setFormType] = useState<FormType | null>(null)
  const [formName, setFormName] = useState<string | null>(null)
  const [schema, setSchema] = useState<JsonSchema>({})
  const [uiSchema, setUiSchema] = useState<UISchemaElement>({
    type: "VerticalLayout",
    elements: [],
  } as UISchemaElement)
  const [data, setData] = useState<Record<string, any>>({})
  const [saveId, setSaveId] = useState<string | null>(null)
  const [showValidationErrors, setShowValidationErrors] = useState<boolean>(
    showValidationErrorsProp,
  )
  const [mode, setMode] = useState<FormRenderMode>(modeProp)
  const [formErrors, setFormErrors] = useState<Record<string, string>>({})

  const previousFormRef = useRef<{
    schema: any
    uiSchema: any
    data: any
    mode: FormRenderMode
    name: string
    formType: FormType | null
    saveId: string | null
  } | null>(null)

  useEffect(() => {
    setShowValidationErrors(showValidationErrorsProp)
  }, [showValidationErrorsProp])

  useEffect(() => {
    setMode(modeProp)
  }, [modeProp])

  /**
   * Updates the form data by merging new data with the previous state.
   */
  const updateFormData = (newData: Record<string, any>) => {
    setData((prevData) => ({
      ...prevData,
      ...newData,
    }))
  }

  /**
   * Fetches a template and updates the context state accordingly.
   */
  const fetchTemplate = async (template: any) => {
    if (!details) return
    try {
      const response = await dispatch(
        fetchFormByCaseIdWithMergeData({
          formId: template.id,
          caseId: details.caseId,
          getSavedData: true,
          patientId: details.patientId,
          formType: FormType.SMARTRX,
          uassistRound: 0,
        }),
      )
      let template_key = ""
      switch (template.name) {
        case "Limited Treatment":
          template_key = "limited_treatment_template_instructions"
          break
        case "Open Bite":
          template_key = "open_bite_template_instructions"
          break
        case "Deep Bite":
          template_key = "deep_bite_template_instructions"
          break
        case "Crowding":
          template_key = "crowding_template_instructions"
          break
        case "Spacing":
          template_key = "spacing_template_instructions"
          break
        default:
          return
      }
      const res = (response as any).payload
      !data.smart_rx_template && setData((prevData) => ({
        ...prevData,
        [template_key]: parseToObject(res?.default_data)
          ?.case_specific_instructions,
      }))
      const rawUISchema = parseToObject(res.ui_schema.schema_data)
      const renderedTemplateValue = {
        showTemplate: false,
        template: template.name,
        schema: parseToObject(res.form_schema.schema_data),
        uiSchema: {
          ...rawUISchema,
          elements: [...rawUISchema.elements].filter(
            (element: any) => element.label !== "Additional Instructions",
          ),
        },
        data: parseToObject(res.default_data),
        defaultData: parseToObject(res.default_data),
        errors: [],
      }
      setRenderedTemplate(renderedTemplateValue)
    } catch (error) {
      // Optionally handle error (e.g., set error state)
    }
  }

  /**
   * Routes to the edit template form, updating context state as needed.
   */
  const routeToEditTemplateForm = async (template: {
    value: string
    id: string
  }) => {
    if (!details) return
    try {
      setRenderedTemplate((prev) => {
        return prev ? { ...prev, isTransitioning: true } : null
      })
      const response = await dispatch(
        fetchFormByCaseIdWithMergeData({
          formId: template.id,
          caseId: details.caseId,
          getSavedData: true,
          patientId: details.patientId,
          formType: FormType.SMARTRX,
          uassistRound: 0,
        }),
      )
      const res = (response as any).payload
      if (renderedTemplate?.template === template.value) {
        previousFormRef.current = {
          schema: schema,
          uiSchema: uiSchema,
          data: data,
          mode: mode,
          name: formName ?? "",
          formType: formType,
          saveId: saveId,
        }
        setShowValidationErrors(false)
        const rawUISchema = parseToObject(res.ui_schema.schema_data)
        setFormName(renderedTemplate.template)
        setMode(FormRenderMode.EDIT)
        setSchema(parseToObject(res.form_schema.schema_data))
        setUiSchema({
          ...rawUISchema,
          elements: [...rawUISchema.elements].filter(
            (element: any) => element.label !== "Additional Instructions",
          ),
        })
        setData(parseToObject(res.default_data))
        setFormType(FormType.SMARTRX)
        setSaveId(res.save_id || null)
        setRenderedTemplate((prev) =>
          prev ? { ...prev, showTemplate: true } : null,
        )
      } else {
        // If not the same template, you may want to fetch and set new template data here.
      }
    } catch (error) {
      // Optionally handle error
    } finally {
      
      // Delay to ensure preventing any other cross update of state before next render
      setTimeout(() => {
        // renderedTemplate.isTransitioning = false;
        setRenderedTemplate((prev) => {
          return prev ? { ...prev, isTransitioning: false } : null
        })
      }, 100) 
    }
  }

  useEffect(() => {
    if (
      data?.smart_rx_template_id?.value &&
      renderedTemplate?.template !== data?.smart_rx_template_id.value &&
      details
    ) {
      fetchTemplate(
        getFormByName(
          formTemplateList,
          FormType.SMARTRX,
          data?.smart_rx_template_id.value,
        ),
      )
    }
  }, [data?.smart_rx_template_id])

  /**
   * Routes back to the previous form, restoring previous state.
   */
  const routeBackToForm = (formSaved: boolean = false) => {
    setRenderedTemplate((prev) => {
      return prev ? { ...prev, isTransitioning: true } : null
    })
    setRenderedTemplate((prev) => {
      return formSaved
        ? { ...prev, data: data, defaultData: data, showTemplate: false }
        : prev
        ? { ...prev, data: prev.defaultData, showTemplate: false }
        : null
    })
    if (previousFormRef.current) {
      setMode(previousFormRef.current.mode)
      setFormName(previousFormRef.current.name)
      setSchema(previousFormRef.current.schema)
      setUiSchema(previousFormRef.current.uiSchema)
      setData(previousFormRef.current.data)
      setFormType(previousFormRef.current.formType)
      setSaveId(previousFormRef.current.saveId)
    }

    // Delay to ensure preventing any other cross update of state before next render
    setTimeout(() => {
      setRenderedTemplate((prev) => {
        return prev ? { ...prev, isTransitioning: false } : null
      })
    }, 100) 
  }

  return (
    <FormContext.Provider
      value={{
        details,
        formType,
        setFormType,
        formErrors,
        setFormErrors,
        formName,
        setFormName,
        schema,
        setSchema,
        uiSchema,
        setUiSchema,
        data,
        setData,
        showValidationErrors,
        setShowValidationErrors,
        mode,
        setMode,
        fetchTemplate,
        routeToEditTemplateForm,
        renderedTemplate,
        setRenderedTemplate,
        routeBackToForm,
        saveId,
        setSaveId,
      }}
    >
      {children}
    </FormContext.Provider>
  )
}

/**
 * Custom hook to access the FormContext. Throws if used outside a FormProvider.
 */
export const useFormContext = () => {
  const context = useContext(FormContext)
  if (context === undefined) {
    throw new Error("useFormContext must be used within a FormProvider")
  }
  return context
}
